import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Mail } from "lucide-react";

const LOGO_URL =
  "/logo-brand.png";

const DMS_LIST = [
  { name: "CDK Global", method: "via DealerVault / Fortellis certified data feed" },
  { name: "Reynolds & Reynolds", method: "via DealerVault / IntegraLink certified data feed" },
  { name: "Tekion Automotive Retail Cloud", method: "direct REST API (Tekion Openlane)" },
  { name: "Dealertrack DMS (Cox Automotive)", method: "direct API + Xtime service-lane feed" },
  { name: "Dominion VUE", method: "direct cloud API" },
  { name: "PBS Systems", method: "direct cloud API" },
  { name: "Auto/Mate (DealerSocket)", method: "direct API + DealerVault fallback" },
  { name: "DealerBuilt", method: "included open data feed" },
  { name: "Autosoft", method: "via DealerVault CSV feed" },
  { name: "Xtime (service lane)", method: "direct API" },
];

const DATA_FIELDS = [
  "Customer full name",
  "Customer mobile phone (E.164 format preferred)",
  "Vehicle year / make / model / VIN (last 8 OK)",
  "Declined or Recommended-Not-Sold service description",
  "Estimated price per line item (optional but improves ROI reporting)",
  "Repair Order (RO) number",
  "RO close date / decline date",
  "Assigned Service Advisor name",
];

export default function ApiLetterPage() {
  const [s, setS] = useState(null);
  const [selectedDms, setSelectedDms] = useState("");
  useEffect(() => {
    api.get("/settings").then((r) => {
      setS(r.data);
      if (r.data?.dms_platform) {
        const match = DMS_LIST.find((d) =>
          d.name.toLowerCase().includes((r.data.dms_platform || "").toLowerCase())
        );
        if (match) setSelectedDms(match.name);
      }
    }).catch(() => {});
  }, []);
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const mailto = `mailto:?subject=${encodeURIComponent(
    "Decline Drive — DMS Data Access Letter"
  )}&body=${encodeURIComponent(
    "Please see attached: Decline Drive data-access reference letter.\n\nContact: " +
    (s?.owner_name || "James Ayers") + " · " + (s?.owner_email || "jayers.me@gmail.com")
  )}`;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="DMS Data Access Letter" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Collateral</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            DMS Data Access Letter
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            A one-page reference letter explaining exactly which DMS platforms and data feeds Decline Drive
            connects to. Hand this to the dealer principal, IT, or their DMS vendor rep.
          </p>
        </div>
        <div className="flex gap-2">
          <a href={mailto} data-testid="api-letter-email-link">
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Email letter
            </Button>
          </a>
          <Button onClick={() => window.print()} data-testid="print-api-letter-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print letter
          </Button>
        </div>
      </div>

      <div
        id="api-letter"
        className="bg-white text-neutral-900 max-w-[8.5in] mx-auto shadow-2xl border border-border rounded-lg overflow-hidden"
      >
        {/* Header band */}
        <div className="bg-navy text-white px-10 py-6 flex items-center gap-4">
          <img
            src={LOGO_URL}
            alt="Decline Drive"
            className="w-14 h-14 object-contain"
          />
          <div className="flex-1">
            <div className="font-display font-black text-2xl tracking-tight">
              Decline Drive™ — DMS Data Access Reference
            </div>
            <div className="text-xs text-[hsl(var(--gold-light))] mt-1">
              Ayers Enterprises · Nightly Declined-Service Recovery Platform
            </div>
          </div>
        </div>
        <div className="gold-divider" />

        {/* Body */}
        <div className="px-10 py-8 text-sm leading-relaxed space-y-5">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono-label text-neutral-500 mb-1">Prepared for</div>
              <div className="font-semibold text-base">
                {s?.dealership_name || "________________________"}
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Dealership / Service Director / DMS Vendor
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-label text-neutral-500 mb-1">Date</div>
              <div className="font-semibold">{today}</div>
            </div>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">To whom it may concern,</div>
            <p>
              This letter certifies that <b>Ayers Enterprises</b>, operating the{" "}
              <b>Decline Drive™</b> platform, requires a nightly export of declined and recommended-not-sold
              service records from the above dealership&apos;s Dealer Management System (&quot;DMS&quot;) in
              order to deliver the automated customer follow-up services engaged by the dealership. The dealer
              has authorized this data feed pursuant to Section 7 (DMS Data Access) of the executed Service
              Agreement.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">1. Supported DMS Platforms &amp; Connection Method</div>
            <p className="mb-2 text-neutral-700">
              Decline Drive is compatible with every major automotive DMS in the United States and Canada.
              We use the following certified integration paths — no direct DMS credentials are stored by
              Decline Drive:
            </p>
            <div className="mb-3 flex items-center gap-2 no-print">
              <label className="font-mono-label text-neutral-500">Highlight this dealer&apos;s DMS:</label>
              <select
                value={selectedDms}
                onChange={(e) => setSelectedDms(e.target.value)}
                className="border border-neutral-300 rounded-md px-2 py-1 text-xs bg-white text-neutral-800"
                data-testid="dms-selector"
              >
                <option value="">— Select a DMS —</option>
                {DMS_LIST.map((d) => (
                  <option key={d.name} value={d.name}>{d.name}</option>
                ))}
                <option value="__other__">Other / Not Listed</option>
              </select>
            </div>
            {selectedDms && selectedDms !== "__other__" && (
              <div className="mb-3 p-3 rounded-md bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))] text-sm">
                <b>This dealer uses:</b> {selectedDms}. That platform is connected via{" "}
                <em>{DMS_LIST.find((d) => d.name === selectedDms)?.method}</em>.
              </div>
            )}
            {selectedDms === "__other__" && (
              <div className="mb-3 p-3 rounded-md bg-neutral-100 border border-neutral-300 text-sm">
                <b>This dealer&apos;s DMS is not listed above.</b> We&apos;ll begin with the manual CSV
                upload fallback (see Section 3) and coordinate directly with their DMS vendor to explore a
                nightly automated feed.
              </div>
            )}
            <table className="w-full text-xs border border-neutral-300 mt-2">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="text-left px-3 py-2 border-b border-neutral-300 w-1/3">DMS Platform</th>
                  <th className="text-left px-3 py-2 border-b border-neutral-300">Connection Method</th>
                </tr>
              </thead>
              <tbody>
                {DMS_LIST.map((d) => {
                  const isSelected = d.name === selectedDms;
                  return (
                    <tr
                      key={d.name}
                      className={`border-b border-neutral-200 last:border-b-0 ${
                        isSelected ? "bg-[hsl(var(--gold))]/15" : ""
                      }`}
                    >
                      <td className={`px-3 py-2 font-semibold ${isSelected ? "text-neutral-900" : ""}`}>
                        {isSelected && <span className="mr-1">★</span>}
                        {d.name}
                      </td>
                      <td className="px-3 py-2 text-neutral-700">{d.method}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-neutral-500 mt-2 italic">
              Not listed? Manual CSV upload is always available as a fallback — see Section 3.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">2. Preferred: Certified Aggregator Feed</div>
            <p>
              For CDK-, Reynolds-, or Auto/Mate-hosted dealerships, Decline Drive receives data via a
              certified DMS aggregator — typically{" "}
              <b>DealerVault (a subsidiary of Authenticom, Inc.)</b>, <b>Fortellis</b> (CDK-owned marketplace),
              or <b>IntegraLink</b>. These aggregators are the same vetted middleware layer used by Xtime,
              VinSolutions, Kimoby, myKaarma, and hundreds of other automotive-industry SaaS vendors. The
              dealership signs a one-time <em>DMS Data Consent Form</em> supplied by the aggregator, after
              which the nightly file is delivered directly to Decline Drive&apos;s dedicated SFTP endpoint
              between 2:00 – 4:00 AM local time.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">3. Fallback: Manual Nightly CSV</div>
            <p>
              For dealerships on legacy systems without cloud connectivity, Decline Drive accepts a manual
              CSV upload via the in-app <em>Nightly Import</em> button. The system auto-matches 50+ column
              name variants across DMS vendors — see Section 5 for expected fields.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">4. Optional: Direct Cloud DMS API</div>
            <p>
              For cloud-native DMS platforms (Tekion, Dominion VUE, PBS, Dealertrack, DealerSocket, DealerBuilt),
              Decline Drive can connect via the DMS vendor&apos;s own REST API. The dealership authorizes
              read-only access to the &quot;Recommended Services&quot; and &quot;Repair Order&quot; endpoints
              via a 5-minute consent form issued by the DMS vendor.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">5. Data Fields Requested</div>
            <p className="mb-2 text-neutral-700">
              Decline Drive requests <b>only</b> the following data fields, extracted from repair orders
              closed or written in the previous 24 hours. No financial account data, no employee data, and
              no other customer records are collected.
            </p>
            <ul className="list-disc ml-5 space-y-0.5 text-neutral-800 text-sm">
              {DATA_FIELDS.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">6. Data Handling &amp; Compliance</div>
            <p>
              All data is transmitted over TLS 1.2+ and stored in an encrypted MongoDB cluster.
              Decline Drive uses the data <b>solely</b> to deliver the SMS follow-up services contracted by
              the dealership. Data is never resold, shared with third parties, or used for advertising.
              Customer opt-outs (&quot;STOP&quot; replies) are honored automatically and permanently across
              all future campaigns, in compliance with the Telephone Consumer Protection Act (TCPA), the CTIA
              Messaging Principles &amp; Best Practices, and applicable state privacy laws.
            </p>
          </div>

          <div>
            <div className="font-display font-bold text-base mb-1">7. What&apos;s Required From The DMS Vendor</div>
            <ul className="list-decimal ml-5 space-y-1 text-neutral-800">
              <li>
                Enable / schedule the dealership&apos;s existing <em>Declined Services</em> or{" "}
                <em>Recommended Not Sold</em> canned report (most DMS platforms already have this report
                built-in and used by service directors weekly).
              </li>
              <li>
                Deliver the export nightly to the DealerVault / Fortellis / IntegraLink pickup queue —{" "}
                <b>or</b>&nbsp; directly to Decline Drive&apos;s SFTP endpoint (credentials issued during
                onboarding).
              </li>
              <li>
                Countersign the <em>DMS Data Consent Form</em> supplied by the aggregator, on behalf of the
                dealership.
              </li>
            </ul>
          </div>

          {/* Signature block */}
          <div className="pt-8 border-t border-neutral-200 mt-2">
            <p className="mb-6 text-neutral-700">
              For questions about this data access request, DMS vendor coordination, or technical
              specifications, please contact:
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="font-display font-bold">{s?.owner_name || "James Ayers"}</div>
                <div className="text-sm text-neutral-700">Founder / Principal</div>
                <div className="text-sm text-neutral-700">Ayers Enterprises</div>
                <div className="text-sm text-neutral-700 mt-1">{s?.owner_email || "jayers.me@gmail.com"}</div>
                {s?.owner_phone && (
                  <div className="text-sm text-neutral-700">{s.owner_phone}</div>
                )}
                <div className="mt-6 border-b border-neutral-400 h-8" />
                <div className="text-[0.65rem] uppercase tracking-widest text-neutral-500 mt-1">
                  Authorized signature — Ayers Enterprises
                </div>
              </div>
              <div>
                {s?.service_manager_name ? (
                  <>
                    <div className="font-display font-bold">{s.service_manager_name}</div>
                    <div className="text-sm text-neutral-700">Service Manager</div>
                    <div className="text-sm text-neutral-700">
                      {s.dealership_name || "________________________"}
                    </div>
                    {s.service_manager_phone && (
                      <div className="text-sm text-neutral-700 mt-1">{s.service_manager_phone}</div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-display font-bold">Service Manager</div>
                    <div className="text-sm text-neutral-700">
                      {s?.dealership_name || "________________________"}
                    </div>
                    <div className="text-sm text-neutral-500 italic mt-1">
                      (Add name / phone in Settings → Dealer Information)
                    </div>
                  </>
                )}
                <div className="mt-6 border-b border-neutral-400 h-8" />
                <div className="text-[0.65rem] uppercase tracking-widest text-neutral-500 mt-1">
                  On-site escalation contact
                </div>
              </div>
            </div>

            {/* Acknowledgment / signature row for the Dealer Principal */}
            <div className="mt-8 pt-6 border-t border-neutral-200">
              <div className="font-display font-bold">Acknowledged</div>
              <div className="text-sm text-neutral-700">
                {s?.dealership_name || "________________________"}
              </div>
              <div className="text-sm text-neutral-700">Service Director / Dealer Principal</div>
              <div className="mt-6 border-b border-neutral-400 h-8 max-w-md" />
              <div className="text-[0.65rem] uppercase tracking-widest text-neutral-500 mt-1">
                Signature
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <div className="border-b border-neutral-400 h-6" />
                  <div className="text-[0.6rem] uppercase text-neutral-500">Printed name</div>
                </div>
                <div>
                  <div className="border-b border-neutral-400 h-6" />
                  <div className="text-[0.6rem] uppercase text-neutral-500">Date</div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 text-[0.6rem] text-neutral-500 border-t border-neutral-200 text-center mt-4">
            © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
            This document is a data-access reference, not a legal contract. The dealership&apos;s executed
            Service Agreement governs all terms.
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
